import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const handDrawnAnnotationSkill: Skill = {
    name: SKILL_NAMES.handDrawnAnnotation,
    kind: 'codeo',
    description:
        'Draw a hand-made mark around or toward something — a lasso circle, an arrow, a bracket, ' +
        'a scribbled box — as though someone were pointing at the frame while it played.',
    instructions: `# Hand-Drawn Annotation

An imperfect ink mark draws itself onto the frame: a circle lassoing a figure, an arrow pointing at a card, a bracket gathering three items, a box around a word. It reads as a person marking up the graphic *live*, which is a completely different voice from a system highlighting something — warmer, more argumentative, and much harder to ignore.

The technique is a dash-offset draw on a deliberately wobbly SVG path. The wobble is the whole thing: a geometrically perfect circle reads as a UI element, not as a hand.

## Markup

Author the path with slight asymmetry and let it overshoot where it closes, the way a real lasso does. Keep the stroke round-capped.

\`\`\`html
<svg id="lasso" viewBox="0 0 600 320" style="position: absolute; left: 660px; top: 380px; width: 600px; height: 320px; overflow: visible;">
  <path id="lasso-path" d="M300 22 C142 18 34 96 40 168 C46 244 176 300 316 296 C452 292 560 232 556 158 C552 88 442 26 296 24 C244 23 208 32 180 46"
    fill="none" stroke="var(--l5-color-2, #ef4444)" stroke-width="9" stroke-linecap="round" />
</svg>
\`\`\`

The final \`C\` segment carries past the start point — that overshoot is what makes it read as drawn rather than closed.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const lassoPath = document.querySelector('#lasso-path');
const lassoLength = Math.ceil(lassoPath.getTotalLength()) + 2;
const MARK = 1.4;

gsap.set(lassoPath, { strokeDasharray: lassoLength, strokeDashoffset: lassoLength + 1 });

tl.to(lassoPath, {
    strokeDashoffset: 0,
    duration: 0.72,
    ease: 'power2.out',
}, MARK);

window.__timelines = { main: tl };
\`\`\`

\`power2.out\` starts fast and decelerates, which is how a hand actually moves — a linear draw reads as a machine plotting.

## Adapt

- **Arrow**: a curved shaft drawn first, then two short head strokes landing 0.1s after it. The head arriving late is what makes the arrow feel thrown.
- **Bracket**: a tall curly or square bracket gathering a group, drawn top to bottom, with the label fading in beside it once the stroke completes.
- **Box**: four segments as one path with rounded, overshooting corners. Never use a \`<rect>\` — the corners are exactly what betray it.
- **Underline**: see \`${SKILL_NAMES.markerEmphasis}\` for the version that tracks a word in a line; this skill is for marks in open frame space.
- **Two strokes**: real annotation often doubles back. A second, shorter stroke drawn 0.15s after the first over the same area reads markedly more human.
- **Ink weight**: 7–11px at 1920 wide. Thinner reads as a diagram line; thicker as a marker, which is a different register again.

## Pitfalls

- \`getTotalLength()\` must be read from the live element after layout, so the dash setup belongs in the script rather than hardcoded in the markup. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- The SVG needs \`overflow: visible\` or a stroke that overshoots its viewBox gets clipped at the seam.
- Don't draw the mark before the thing it marks has arrived. The annotation is a reaction; it lands after its subject, ideally on the narration word that names it.
- **One mark per graphic.** Not one circle plus an arrow — one mark, total. A lasso *and* a pointer is a whiteboard, and the second mark always costs the first its authority, because a frame with two things circled has nothing singled out.
- **Every selector in the timeline has to exist in the markup.** A tween on a heading or label you never wrote is silent — no error, no lint finding, just copy that is missing from the finished frame. The annotation is a reaction to something; check the thing it reacts to is actually on screen.
- Resist making the path neat. If it looks like it could have been a \`<circle>\`, redraw it with more asymmetry.
- The mark must not be the only thing moving in the scene — it draws in about 0.7s and then the frame is static again.
- Erase it if the scene continues past its point: reversing the dash offset un-draws the stroke and hands the frame back.`,
};
